/**
 * ユーザープロファイル（画面状態）復元ガード
 *
 * user_profile の AUTO 復元は「当時と今でデータ状態が変わっていても画面を壊さない」ことが最重要要件。
 * ここでは復元を安全側にフォールバックさせるための共通ユーティリティを提供する。
 * - JSON パース失敗 / null は fallback
 * - ページ番号が総ページ数を超えたら 1 ページ目へ
 * - 保存された選択肢が現在の選択肢に無ければ既定値へ
 * - 未知キーは無視、欠損キーは各画面のデフォルトで補う（マージ）
 */

/**
 * profileValue（JSON文字列）を安全にパースする。
 * パース失敗・undefined・null の場合は fallback を返す。
 */
export const parseProfileValue = <T>(raw: string | undefined | null, fallback: T): T => {
  if (raw == null) {
    return fallback;
  }
  try {
    const parsed = JSON.parse(raw);
    if (parsed == null) {
      return fallback;
    }
    return parsed as T;
  } catch {
    return fallback;
  }
};

/**
 * ページ番号を総ページ数の範囲内へ補正する。
 * 総ページ数を超える（保存後に件数が減った等）場合は 1 ページ目へフォールバック。
 * totalPages が確定してから適用すること。
 *
 * @param savedPage 復元されたページ番号（1始まり想定）
 * @param totalPages 現在の総ページ数
 * @returns 範囲内なら savedPage、超過・不正なら 1
 */
export const clampPage = (savedPage: number, totalPages: number): number => {
  if (!Number.isFinite(savedPage) || savedPage < 1) {
    return 1;
  }
  if (totalPages >= 1 && savedPage > totalPages) {
    return 1;
  }
  return savedPage;
};

/**
 * 保存値が現在の選択肢に存在するかを確認し、無ければ既定値へフォールバックする。
 * 選択肢が変わった（削除された等）場合の安全復元に使う。
 *
 * @param savedValue 復元された値
 * @param options 現在有効な選択肢
 * @param fallback 選択肢に無い場合の既定値
 */
export const resolveOption = <T>(savedValue: T, options: readonly T[], fallback: T): T => {
  return options.includes(savedValue) ? savedValue : fallback;
};

/**
 * 復元値（部分オブジェクト）をデフォルト state にマージする。
 * - 未知キー（デフォルトに存在しないキー）は無視する
 * - 欠損キー（復元値に無いキー）はデフォルト値で補う
 *
 * @param defaults 各画面のデフォルト state（形の正）
 * @param restored 復元された部分オブジェクト
 */
export const mergeWithDefaults = <T extends Record<string, unknown>>(
  defaults: T,
  restored: Partial<T> | null | undefined,
): T => {
  if (restored == null || typeof restored !== 'object') {
    return { ...defaults };
  }
  const result: T = { ...defaults };
  (Object.keys(defaults) as (keyof T)[]).forEach((key) => {
    const value = restored[key];
    if (value !== undefined) {
      result[key] = value as T[keyof T];
    }
  });
  return result;
};
